import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Lock, ShieldAlert, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { adminApi, clearSession } from "@/lib/auth";
import { logError } from "@/lib/log";

const BG_URL =
  "https://images.unsplash.com/photo-1554684726-0830be8cdbcd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMHdoaXRlJTIwYXJjaGl0ZWN0dXJlJTIwbWluaW1hbHxlbnwwfHx8fDE3Nzg1MzU0OTR8MA&ixlib=rb-4.1.0&q=85";

// Escala de fortaleza: 0 (vacía) → 5 (muy fuerte). Cuenta longitud,
// mayúsculas, minúsculas, números y caracteres especiales. Devuelve un
// objeto con nivel, etiqueta y color para renderizar la barra.
function scorePassword(pw) {
  if (!pw) return { score: 0, level: "empty", label: "", color: "" };
  let s = 0;
  if (pw.length >= 8) s++;
  if (pw.length >= 12) s++;
  if (/[A-Z]/.test(pw)) s++;
  if (/[a-z]/.test(pw)) s++;
  if (/\d/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  // Colapsamos a 3 niveles finales para la UI.
  if (s <= 2) return { score: s, level: "weak", label: "Débil", color: "bg-rose-500" };
  if (s <= 4) return { score: s, level: "medium", label: "Media", color: "bg-amber-500" };
  return { score: s, level: "strong", label: "Fuerte", color: "bg-emerald-500" };
}

/**
 * Pantalla de cambio de contraseña — sirve para dos casos:
 *   - Cambio FORZADO tras recuperación por email (`must_change_password`).
 *   - Cambio VOLUNTARIO desde el perfil del usuario (con botón cancelar).
 *
 * El rol (instructor o gestor) se detecta por la URL para elegir los
 * endpoints correctos. Valida coincidencia + mínimo 4 caracteres.
 */
export default function CambiarContrasenaScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin/");
  const meEndpoint = isAdmin ? "/auth/me-admin" : "/auth/me-instructor";
  const changeEndpoint = isAdmin
    ? "/admin/change-password"
    : "/instructor/change-password";
  const backHome = isAdmin ? "/admin" : "/instructor";
  const loginRoute = isAdmin ? "/admin/login" : "/instructor/login";

  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [loading, setLoading] = useState(false);
  const [mustChange, setMustChange] = useState(false);

  // Indicador visual de fortaleza (débil / media / fuerte).
  const strength = useMemo(() => scorePassword(p1), [p1]);

  // Verificar sesión + must_change_password.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await adminApi.get(meEndpoint);
        if (cancelled) return;
        setMustChange(!!r.data?.must_change_password);
      } catch (e) {
        logError("No se pudo verificar la sesión", e);
        navigate(loginRoute);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, meEndpoint, loginRoute]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!p1 || !p2) {
      toast.error("Escribe la nueva contraseña en los dos campos.");
      return;
    }
    if (p1 !== p2) {
      toast.error("Las contraseñas no coinciden.");
      return;
    }
    if (p1.length < 4) {
      toast.error("La contraseña debe tener al menos 4 caracteres.");
      return;
    }
    setLoading(true);
    try {
      await adminApi.post(changeEndpoint, {
        new_password: p1,
        new_password_confirm: p2,
      });
      toast.success("Contraseña actualizada. Vuelve a iniciar sesión.");
      // Cerramos sesión para forzar login con la nueva contraseña.
      clearSession();
      navigate(loginRoute);
    } catch (err) {
      logError("Error cambiando contraseña", err);
      toast.error(
        err.response?.data?.detail || "No se pudo actualizar la contraseña."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="relative min-h-screen w-full flex items-center justify-center px-6 py-12"
      data-testid="change-password-screen"
    >
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${BG_URL})` }}
        aria-hidden
      />
      <div className="absolute inset-0 bg-white/85 backdrop-blur-xl" aria-hidden />
      <div className="relative z-10 w-full max-w-md">
        {!mustChange && (
          <button
            onClick={() => navigate(backHome)}
            className="inline-flex items-center gap-2 text-sm font-medium text-zinc-700 hover:text-zinc-950 transition-colors mb-6"
            data-testid="change-password-back"
          >
            <ChevronLeft className="h-4 w-4" />
            Volver
          </button>
        )}
        <div className="bg-white border border-zinc-200 rounded-lg shadow-[0_1px_0_rgba(0,0,0,0.04),0_24px_60px_-24px_rgba(0,0,0,0.18)] p-8 md:p-10">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-zinc-500 mb-8">
            <span className="inline-block h-2 w-2 bg-black" />
            {mustChange ? "Cambio obligatorio" : "Cambiar contraseña"}
          </div>
          {mustChange && (
            <div
              className="mb-6 flex items-start gap-3 rounded-md border-2 border-amber-500 bg-amber-50 px-4 py-3 text-sm text-amber-900"
              data-testid="change-password-alert"
            >
              <ShieldAlert className="h-5 w-5 shrink-0 mt-0.5" />
              <div>
                Has entrado con una contraseña temporal enviada por correo.
                Antes de continuar debes elegir una contraseña nueva.
              </div>
            </div>
          )}
          <h1 className="heading-font text-3xl sm:text-4xl font-extrabold text-zinc-950 leading-[0.95]">
            Nueva contraseña
          </h1>
          <p className="mt-3 text-zinc-600 text-base">
            Mínimo 4 caracteres. Escríbela dos veces para confirmar.
          </p>
          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Nueva contraseña
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-400" />
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={p1}
                  onChange={(e) => setP1(e.target.value)}
                  placeholder="••••••••"
                  className="touch-input pl-12 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
                  data-testid="change-password-new"
                  autoFocus
                />
              </div>
              {/* Indicador de fortaleza */}
              {p1 && (
                <div
                  className="mt-2"
                  data-testid="password-strength"
                  data-strength={strength.level}
                >
                  <div className="flex gap-1 mb-1.5">
                    {[0, 1, 2].map((i) => {
                      const filled =
                        (strength.level === "weak" && i === 0) ||
                        (strength.level === "medium" && i <= 1) ||
                        (strength.level === "strong" && i <= 2);
                      return (
                        <div
                          key={i}
                          className={`h-1.5 flex-1 rounded ${
                            filled ? strength.color : "bg-zinc-200"
                          } transition-colors`}
                        />
                      );
                    })}
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span
                      className={`font-semibold ${
                        strength.level === "weak"
                          ? "text-rose-600"
                          : strength.level === "medium"
                            ? "text-amber-600"
                            : "text-emerald-700"
                      }`}
                    >
                      Fortaleza: {strength.label}
                    </span>
                    {strength.level === "weak" && (
                      <span className="text-zinc-500">
                        Añade mayúsculas, números o símbolos
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Repite la contraseña
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-400" />
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={p2}
                  onChange={(e) => setP2(e.target.value)}
                  placeholder="••••••••"
                  className="touch-input pl-12 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
                  data-testid="change-password-confirm"
                />
              </div>
              {p2 && p1 && p1 !== p2 && (
                <p className="mt-1 text-xs text-rose-600" data-testid="change-password-mismatch">
                  Las contraseñas no coinciden.
                </p>
              )}
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-14 text-base font-semibold bg-zinc-950 hover:bg-zinc-800 text-white rounded-md transition-colors mt-4"
              data-testid="change-password-submit"
            >
              {loading ? "Guardando…" : "Guardar nueva contraseña"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
